import { Component, OnInit } from '@angular/core';
import { DataService } from '../../data.service';
import {
  UzemszunetAllapotKod, UzemszunetForras, UzemszunetService, idoablakSzoveg, datumSzoveg, kozelgo,
} from './uzemszunet.service';

/*  ÜZEMSZÜNET-FIGYELŐ – szerviz-kártya (NAPLO → Szervíz).
 *
 *  Listázza a 4 külső forrást (NAV, Ügyfélkapu/KAÜ, bank-aggregátor, e-Önkormányzat) a
 *  valós állapotukkal (`GET /api/uzemszunet/allapot`). Forrásonként adminisztrátori kézi
 *  felülírás állítható be (állapot + üzenet + időablak) `PUT /api/uzemszunet/kezi/:forras`-szal,
 *  és törölhető `DELETE`-tel. Az admin-jogot a KISZOLGÁLÓ ellenőrzi; a gombok itt mindig
 *  látszanak, hiba esetén toast. */

interface ForrasSzerk {
  allapot: UzemszunetAllapotKod;
  uzenet: string;
  idoablak_kezd: string; // datetime-local input érték
  idoablak_veg: string;
  nyitva: boolean;       // a kézi szerkesztő panel ki van-e nyitva
}

@Component({
  selector: 'app-naplo-uzemszunet',
  templateUrl: './uzemszunet.component.html',
  styleUrls: ['./uzemszunet.component.scss'],
})
export class UzemszunetComponent implements OnInit {
  loading = true;
  forrasok: UzemszunetForras[] = [];
  vanAktiv = false;

  /** Forrásonkénti szerkesztő-állapot. */
  szerk: Record<string, ForrasSzerk> = {};
  /** Forrásonként épp fut-e mentés/törlés (gomb-tiltáshoz). */
  foglalt: Record<string, boolean> = {};

  // A szöveges helpereket a template is használja.
  idoablakSzoveg = idoablakSzoveg;
  kozelgo = kozelgo;
  datumSzoveg = datumSzoveg;

  readonly allapotOpciok: { ertek: UzemszunetAllapotKod; cim: string }[] = [
    { ertek: 'ok', cim: 'Rendben (nincs üzemszünet)' },
    { ertek: 'tervezett', cim: 'Tervezett karbantartás' },
    { ertek: 'kieses', cim: 'Üzemszünet / kiesés' },
  ];

  constructor(private uf: UzemszunetService, private data: DataService) {}

  async ngOnInit() { await this.betolt(); }

  async betolt() {
    this.loading = true;
    const a = await this.uf.allapot(true);
    this.forrasok = a.forrasok || [];
    this.vanAktiv = !!a.van_aktiv;
    for (const f of this.forrasok) {
      // A már kinyitott panelt nyitva hagyjuk újratöltéskor.
      const elozo = this.szerk[f.forras];
      this.szerk[f.forras] = {
        allapot: f.allapot || 'ok',
        uzenet: f.uzenet || '',
        idoablak_kezd: this.inputDatum(f.idoablak_kezd),
        idoablak_veg: this.inputDatum(f.idoablak_veg),
        nyitva: !!elozo?.nyitva,
      };
    }
    this.loading = false;
  }

  /** Állapot-jelző színe (badge). */
  allapotSzin(a: UzemszunetAllapotKod): string {
    if (a === 'ok') return 'success';
    if (a === 'tervezett') return 'warning';
    return 'danger';
  }
  allapotCim(a: UzemszunetAllapotKod): string {
    if (a === 'ok') return 'Rendben';
    if (a === 'tervezett') return 'Tervezett';
    return 'Kiesés';
  }

  /** A jelzés forrásának emberi neve (auto/futás/e-mail/kézi). */
  forrasTipusCim(t?: string): string {
    switch (t) {
      case 'auto': return 'Automatikus mérés';
      case 'futas': return 'Lekérés-futás';
      case 'email': return 'E-mail értesítés';
      case 'kezi': return 'Kézi beállítás';
      default: return 'Ismeretlen';
    }
  }
  forrasTipusIkon(t?: string): string {
    switch (t) {
      case 'auto': return 'pulse-outline';
      case 'futas': return 'sync-outline';
      case 'email': return 'mail-outline';
      case 'kezi': return 'hand-left-outline';
      default: return 'help-outline';
    }
  }

  panelValt(f: UzemszunetForras) {
    const sz = this.szerk[f.forras];
    if (sz) sz.nyitva = !sz.nyitva;
  }

  /** Kézi felülírás mentése (PUT). */
  async ment(f: UzemszunetForras) {
    const sz = this.szerk[f.forras];
    if (!sz) return;
    this.foglalt[f.forras] = true;
    try {
      await this.uf.keziMent(f.forras, {
        allapot: sz.allapot,
        uzenet: (sz.uzenet || '').trim(),
        idoablak_kezd: this.isoDatum(sz.idoablak_kezd),
        idoablak_veg: this.isoDatum(sz.idoablak_veg),
      });
      this.data.sendToast(`${f.nev}: kézi felülírás mentve.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'Nem sikerült menteni a felülírást.'), 'error');
    } finally {
      this.foglalt[f.forras] = false;
    }
  }

  /** Kézi felülírás törlése (DELETE) – visszaáll az automatikus állapotra. */
  async torol(f: UzemszunetForras) {
    this.foglalt[f.forras] = true;
    try {
      await this.uf.keziTorol(f.forras);
      this.data.sendToast(`${f.nev}: kézi felülírás törölve.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'Nem sikerült törölni a felülírást.'), 'error');
    } finally {
      this.foglalt[f.forras] = false;
    }
  }

  /** ISO időpont → datetime-local input érték (helyi idő, YYYY-MM-DDTHH:mm). */
  private inputDatum(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => (n < 10 ? '0' : '') + n;
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  /** datetime-local input érték → ISO (vagy null, ha üres/érvénytelen). */
  private isoDatum(ertek: string): string | null {
    if (!ertek) return null;
    const d = new Date(ertek);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  private hibaUzenet(e: any, alap: string): string {
    if (e?.status === 403) return 'Ehhez nincs jogosultságod (csak adminisztrátor).';
    return e?.error?.error || e?.error?.message || alap;
  }
}
